import React, { useEffect, useRef, useState } from "react";
import "./commentModal.scss";
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import IconButtons from "./../IconButtons/IconButtons";
import {
  circle_save_change_icon,
  cross_red_icon,
  delete_active_icon,
  delete_disabled_icon,
  disabled_circle_save_change_icon,
  edit_active_icon,
  edit_disabled_icon,
} from "../../constants/icons";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import {
  clearCommentsList,
  deleteComment,
  editComment,
  getCommentList,
} from "../../store/comments";
import FlipCard from "react-card-flip";
import NoDataAvailable from "../NoDataAvailable/NoDataAvailable";
import BootstrapTooltip from "../BootstrapTooltip/BootstrapTooltip";
import { backendBaseUrl } from "../../config/config";
import Loader from "../Loader/Loader";
import { font_13_icon } from "../../utils/utils";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const utc = require("dayjs/plugin/utc");
const timezone = require("dayjs/plugin/timezone");

dayjs.extend(utc);
dayjs.extend(timezone);
const tz = "Asia/Kolkata";

// Component for Comment Card Front
const CommentCardFront = ({
  comment,
  commentInfo,
  expandedComments,
  wordCount,
  lineLength,
  handleToggleCommentExpand,
  handleInputEditComment,
  originalCommentInfo,
  submitRowEdit,
  handleRowEditClose,
  canEditDeleteComments,
  isBootcampCompleted,
  handleEdit,
  handleCardFlip,
  showResourceTables,
  toggleResourceTable,
  ExpandMore,
  commentResources,
  onResourceViewClick,
}) => {
  const isEditing = comment?.id === commentInfo?.commentId;
  const isExpanded = expandedComments[comment.id];
  const exceedsLimit = doesExceedsWordCountOrLineLength(wordCount, lineLength);
  const showResourceTable = showResourceTables[comment.id];
  const hasResources = comment?.resources?.results?.length > 0;

  return (
    <Box
      id={`comment-front-${comment.id}`}
      className={`comment-show-container ${
        commentInfo?.deleteCommentId && "disabledBox"
      }`}
    >
      <Box className="comment-show-head">
        <Typography variant="font_12_bold_600">
          {comment?.createdBy?.fullName}
        </Typography>
        <Typography variant="font_12" className="grey-text">
          {dayjs(comment?.dateCreated)?.tz(tz)?.format("DD-MMM-YYYY h:mm A")}
        </Typography>
      </Box>
      <Box className="comment-show-body">
        <Box className="comment-description">
          {isEditing ? (
            <TextField
              autoFocus
              multiline
              fullWidth
              id="editComment"
              type="text"
              variant="outlined"
              color="secondary"
              value={commentInfo?.commentDesc}
              onChange={(e) => handleInputEditComment(e?.target?.value)}
            />
          ) : (
            <>
              <Typography variant="font_12">
                <Collapse
                  className={!exceedsLimit ? "adjusted-height" : null}
                  in={isExpanded}
                  collapsedSize={exceedsLimit ? 35 : "auto"}
                >
                  <Box
                    className="ellipsis"
                    sx={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    {getSubstringComment(
                      wordCount,
                      lineLength,
                      expandedComments,
                      comment
                    )}
                  </Box>
                </Collapse>
                {exceedsLimit && (
                  <CommentViewMore
                    onClick={() => handleToggleCommentExpand(comment.id)}
                    expandedComments={expandedComments}
                    comment={comment}
                  />
                )}
              </Typography>
            </>
          )}
        </Box>
        {isEditing ? (
          <Box className="comment-action-buttons">
            <IconButtons
              height={font_13_icon}
              width={font_13_icon}
              image={circle_save_change_icon}
              classList="padding-right-1"
              handleClick={submitRowEdit}
              disabledImage={disabled_circle_save_change_icon}
              disabled={
                originalCommentInfo?.commentDesc?.trim() ===
                commentInfo?.commentDesc?.trim()
              }
            />
            <IconButtons
              height={font_13_icon}
              width={font_13_icon}
              image={cross_red_icon}
              handleClick={handleRowEditClose}
              classList="padding-right-1"
            />
          </Box>
        ) : (
          canEditDeleteComments && (
            <EditDeleteCommentAction
              handleEdit={() => handleEdit(comment?.id, comment?.comment)}
              disabled={isBootcampCompleted}
              handleDelete={() => handleCardFlip(comment?.id, "delete")}
            />
          )
        )}
      </Box>
      {hasResources && (
        <Box onClick={() => toggleResourceTable(comment.id)}>
          <Typography
            variant="font_9_bold"
            color="secondary"
            cursor="pointer"
            sx={{ fontWeight: 600 }}
          >
            {showResourceTable ? "Hide Submissions" : "View Submissions"}
          </Typography>
          <ExpandMore expand={showResourceTable} sx={{ padding: 0 }}>
            <ExpandMoreIcon
              color="secondary"
              sx={{
                fontSize: "1rem",
              }}
            />
          </ExpandMore>
        </Box>
      )}
      <Collapse in={showResourceTable}>
        <Typography mt={1}>{commentResources}</Typography>
      </Collapse>
    </Box>
  );
};

// Component for Resource List
const ResourceList = ({ resources, commentId, onResourceViewClick }) => {
  let attachmentCount = 0;
  let urlCount = 0;

  return (
    <>
      {resources?.map((resource) => {
        let label = "";
        if (resource?.format === "FILE") {
          attachmentCount++;
          label = `Attachment ${attachmentCount}`;
        } else if (resource?.format === "URL") {
          urlCount++;
          label = `URL ${urlCount}`;
        } else {
          label = `Resource ${resource?.id || Math.random()}`;
        }

        const resourceUrl =
          resource?.format === "URL"
            ? resource?.url
            : `${backendBaseUrl}${resource?.url}`;
        const isLastResource = resource === resources[resources.length - 1];

        return (
          <React.Fragment key={resource?.id || `${commentId}-${resource?.url}`}>
            <BootstrapTooltip title={resource?.name}>
              <Button
                onClick={() => onResourceViewClick(resourceUrl)}
                sx={{
                  textTransform: "none",
                  padding: "0.2rem 0.4rem 0.2rem 0rem",
                  minWidth: "auto",
                }}
                color="secondary"
              >
                <Typography variant="font_9_bold">
                  {label}
                  {!isLastResource && ", "}
                </Typography>
              </Button>
            </BootstrapTooltip>
          </React.Fragment>
        );
      })}
    </>
  );
};

const DeleteCommentModal = ({ frontCardHeight, onCancel, onDelete }) => {
  return (
    <>
      <Box className="show-delete" style={{ height: `${frontCardHeight}px` }}>
        <Typography className="delete-dialog-text" variant="font_12_bold_700">
          Are you sure you want to delete this comment?
        </Typography>
        <Box>
          <Button
            disabled={false}
            variant="outlined"
            color="secondary"
            onClick={onCancel}
            className="delTagButtonStyle"
            disableFocusRipple
            disableRipple
            disableTouchRipple
            disableElevation
          >
            <Typography variant="outlineBtnLabel">Cancel</Typography>
          </Button>

          <Button
            disabled={false}
            color="secondary"
            variant="contained"
            onClick={onDelete}
            className="delTagButtonStyle"
            disableFocusRipple
            disableRipple
            disableTouchRipple
            disableElevation
          >
            <Typography variant="outlineBtnLabel">Yes</Typography>
          </Button>
        </Box>
      </Box>
    </>
  );
};

// Helper function to get helper text for comment field
const getCommentHelperText = (commentEmpty, commentLengthError) => {
  if (commentEmpty) {
    return "Comment cannot be empty";
  }
  if (commentLengthError) {
    return "Maximum 500 characters allowed";
  }
  return "";
};

const AddNewComment = ({
  disabled,
  value,
  onChange,
  commentEmpty,
  commentLengthError,
}) => {
  const helperText = getCommentHelperText(commentEmpty, commentLengthError);

  return (
    <>
      <Box sx={{ paddingRight: "0.6rem" }}>
        <TextField
          multiline
          minRows={3}
          disabled={disabled}
          autoFocus
          id="name"
          shrink={false}
          size="small"
          label="Comment"
          type="text"
          fullWidth
          margin="dense"
          variant="outlined"
          value={value}
          onChange={onChange}
          color="secondary"
          className="comment-area"
          error={commentEmpty || commentLengthError}
          helperText={helperText}
        />
      </Box>
    </>
  );
};

const EditDeleteCommentAction = ({ handleEdit, disabled, handleDelete }) => {
  return (
    <>
      <Box className="comment-action-buttons">
        <IconButtons
          width={font_13_icon}
          height={font_13_icon}
          image={edit_active_icon}
          handleClick={handleEdit}
          classList="padding-right-2"
          disabled={disabled}
          disabledImage={edit_disabled_icon}
          disabledTooltip={disabled ? "Bootcamp is completed" : ""}
        />
        <IconButtons
          width={font_13_icon}
          height={font_13_icon}
          image={delete_active_icon}
          handleClick={handleDelete}
          disabled={disabled}
          disabledImage={delete_disabled_icon}
          disabledTooltip={disabled ? "Bootcamp is completed" : ""}
        />
      </Box>
    </>
  );
};

const CommentViewMore = ({ onClick, expandedComments, comment }) => {
  return (
    <>
      <Typography
        variant="font_9_bold"
        color="secondary"
        onClick={onClick}
        cursor="pointer"
        sx={{ fontWeight: 600 }}
      >
        {expandedComments[comment.id] ? "VIEW LESS" : "VIEW MORE"}
      </Typography>
    </>
  );
};

function getSubstringComment(wordCount, lineLength, expandedComments, comment) {
  if (!doesExceedsWordCountOrLineLength(wordCount, lineLength)) {
    return comment?.comment;
  }
  if (expandedComments[comment.id]) {
    return comment?.comment;
  }
  return `${comment?.comment?.substring(0, 100)}...`;
}

function doesExceedsWordCountOrLineLength(wordCount, lineLength) {
  return wordCount > 100 || lineLength > 3;
}

const CommentModal = ({
  open,
  handleClose,
  handleSubmit,
  exerciseId,
  comments,
  handleInputComment,
  commentDetails,
  openComment,
  canEditDeleteComments,
  canAddComment,
  isBootcampCompleted,
  commentLengthError,
}) => {
  //////////// handle view more ////////////////////////////////
  const ExpandMore = styled((props) => {
    const { expand, ...other } = props;
    return <IconButton {...other} />;
  })(({ theme, expand }) => ({
    transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
    marginLeft: "auto",
    transition: theme.transitions.create("transform", {
      duration: theme.transitions.duration.shortest,
    }),
  }));

  const onResourceViewClick = (url) => window.open(url, "_blank");
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state?.commentsList?.loading);
  const [isCommentEmpty, setIsCommentEmpty] = useState(false);
  const initialCommentInfo = {
    commentId: null,
    commentDesc: "",
    deleteCommentId: null,
  };
  const [originalCommentInfo, setOriginalCommentInfo] =
    useState(initialCommentInfo);
  const [commentInfo, setCommentInfo] = useState(initialCommentInfo);
  const [showResourceTables, setShowResourceTables] = useState({});
  const [isFlipped, setIsFlipped] = useState({});
  const [frontCardHeight, setFrontCardHeight] = useState(0);
  const [expandedComments, setExpandedComments] = useState({});

  const handleToggleCommentExpand = (commentId) => {
    setExpandedComments((prevExpanded) => ({
      ...prevExpanded,
      [commentId]: !prevExpanded[commentId],
    }));
  };

  const onSubmit = async () => {
    setIsCommentEmpty(!commentDetails?.trim());
    setExpandedComments(false);
    await handleSubmit(commentDetails?.trim());
  };

  const handleInputChange = (comment) => {
    setIsCommentEmpty(false);
    handleInputComment(comment);
  };

  const handleDeleteComment = async (commentId) => {
    await dispatch(deleteComment({ commentId, exerciseId }));
    setCommentInfo(initialCommentInfo);
    await dispatch(getCommentList({ exerciseId }));
    setIsFlipped(false);
  };

  const handleEdit = (commentId, commentDesc) => {
    setCommentInfo({ ...initialCommentInfo, commentId, commentDesc });
    setOriginalCommentInfo({ ...initialCommentInfo, commentId, commentDesc });
  };

  const handleInputEditComment = (editedValue) => {
    setCommentInfo({ ...commentInfo, commentDesc: editedValue });
  };

  const handleOnClose = () => {
    setCommentInfo(initialCommentInfo);
    setExpandedComments(false);
    setIsCommentEmpty(false);
    setIsFlipped(false);
    handleClose();
    dispatch(clearCommentsList());
  };

  const handleRowEditClose = () => {
    setCommentInfo(initialCommentInfo);
    setOriginalCommentInfo(initialCommentInfo);
  };

  const submitRowEdit = async () => {
    const { commentId, commentDesc } = commentInfo;
    await dispatch(
      editComment({ commentId, comment: commentDesc, exerciseId })
    );
    setCommentInfo(initialCommentInfo);
  };

  const toggleResourceTable = (commentId) => {
    setShowResourceTables((prev) => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  const handleCardFlip = (commentId, type) => {
    if (type === "delete") {
      setCommentInfo({ ...initialCommentInfo, deleteCommentId: commentId });
    } else {
      setCommentInfo(initialCommentInfo);
    }
    setIsFlipped((prev) => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
    const frontCard = document.getElementById(`comment-front-${commentId}`);
    if (frontCard) {
      setFrontCardHeight(frontCard.offsetHeight);
    }
  };

  /////////////////// Scroll to bottom on mount ///////////////////////////////////
  const commentContainerRef = useRef(null);
  useEffect(() => {
    if (commentContainerRef.current) {
      commentContainerRef.current.scrollTop =
        commentContainerRef.current.scrollHeight;
    }
    // Set all comments to be expanded by default
    const initialExpandedComments = {};
    comments?.forEach((comment) => {
      initialExpandedComments[comment.id] = true;
    });
    setShowResourceTables(initialExpandedComments);
  }, [comments]);
  //////////////////////////////////////////////////////////////////////////////////

  const isAddCommentDisabled =
    commentInfo?.commentId ||
    commentInfo?.deleteCommentId ||
    isBootcampCompleted;

  return (
    <>
      {isLoading && <Loader />}
      <Dialog
        open={open}
        onClose={handleOnClose}
        PaperProps={{
          style: {
            minHeight: "40.125rem",
            height: "40.125rem",
            width: "39rem",
            zIndex: 1300,
            borderRadius: "1rem",
            margin: 0,
            padding: 0,
          },
        }}
      >
        <>
          <DialogTitle className="comment-dialog-title">
            <Typography variant="font_16_bold">
              View Submissions/Comments
            </Typography>
          </DialogTitle>
          <DialogContent
            sx={{
              minHeight: "20rem",
              zIndex: 1300,
              borderRadius: "1rem",
              margin: 0,
              padding: "1rem 1.4rem 1.5rem 2rem !important",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <Box className="comment-main-container" ref={commentContainerRef}>
              {comments?.length > 0
                ? comments?.map((comment) => {
                    const wordCount = comment?.comment.length;
                    const countLines = comment?.comment?.split(/\r|\r\n|\n/);
                    const lineLength = countLines?.length;
                    const commentResources =
                      comment?.resources?.results?.length > 0 ? (
                        <ResourceList
                          resources={comment?.resources?.results}
                          commentId={comment.id}
                          onResourceViewClick={onResourceViewClick}
                        />
                      ) : null;

                    return (
                      <FlipCard
                        key={comment.id}
                        isFlipped={isFlipped[comment.id]}
                        flipDirection="vertical"
                      >
                        <CommentCardFront
                          comment={comment}
                          commentInfo={commentInfo}
                          expandedComments={expandedComments}
                          wordCount={wordCount}
                          lineLength={lineLength}
                          handleToggleCommentExpand={handleToggleCommentExpand}
                          handleInputEditComment={handleInputEditComment}
                          originalCommentInfo={originalCommentInfo}
                          submitRowEdit={submitRowEdit}
                          handleRowEditClose={handleRowEditClose}
                          canEditDeleteComments={canEditDeleteComments}
                          isBootcampCompleted={isBootcampCompleted}
                          handleEdit={handleEdit}
                          handleCardFlip={handleCardFlip}
                          showResourceTables={showResourceTables}
                          toggleResourceTable={toggleResourceTable}
                          ExpandMore={ExpandMore}
                          commentResources={commentResources}
                          onResourceViewClick={onResourceViewClick}
                        />
                        <DeleteCommentModal
                          frontCardHeight={frontCardHeight}
                          onCancel={() => handleCardFlip(comment.id, "cancel")}
                          onDelete={() => handleDeleteComment(comment.id)}
                        />
                      </FlipCard>
                    );
                  })
                : comments?.length === 0 && (
                    <Grid container className="no-data-container">
                      <NoDataAvailable
                        imgHeight={4}
                        imgWidth={4.5}
                        message="No Comments Available"
                      />
                    </Grid>
                  )}
            </Box>
            {canAddComment && (
              <AddNewComment
                disabled={isAddCommentDisabled}
                value={commentDetails}
                onChange={(e) => handleInputChange(e?.target?.value)}
                commentEmpty={isCommentEmpty}
                commentLengthError={commentLengthError}
              />
            )}
          </DialogContent>
          <DialogActions className="comment-dialog-action">
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              onClick={handleOnClose}
              className="block-button"
              disableFocusRipple
              disableRipple
              disableTouchRipple
              disableElevation
            >
              <Typography variant="outlineBtnLabel">Cancel</Typography>
            </Button>
            {canAddComment && (
              <Button
                fullWidth
                disabled={isAddCommentDisabled}
                color="secondary"
                variant="contained"
                onClick={onSubmit}
                className="addTagButtonStyle"
                disableFocusRipple
                disableRipple
                disableTouchRipple
                disableElevation
              >
                <Typography variant="outlineBtnLabel">Save</Typography>
              </Button>
            )}
          </DialogActions>
        </>
      </Dialog>
    </>
  );
};

export default CommentModal;
